import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { DateTime } from "luxon";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTranslation } from "react-i18next";
import { getDateLocale, getLuxonLocale } from "@/lib/i18n/dateLocales";

type TimeSlot = {
  id: string;
  label: string;
  startISO: string;
  startSec: number;
  staffId: number;
  lastHour?: { discount_pct: number; regular_price: number; price: number };
};

export default function DateTimeView({
  // NEW / controlled props
  monthJSDate,
  onMonthChange,
  enabledDateISOSet,
  showNoSlotsToday,
  onGoToNearestDate,
  loading,
  error,

  // EXISTING
  dateISO,
  onPickDate,
  timeSlots,
  waitlistPanel,
  selectedSlotId,
  onPickSlot,
  onBack,
  onDone,
}: {
  // NEW / controlled props
  monthJSDate?: Date;
  onMonthChange?: (jsDate: Date) => void;
  enabledDateISOSet: Set<string>;
  showNoSlotsToday: boolean;
  onGoToNearestDate: () => void;
  loading?: boolean;
  error?: boolean;

  // EXISTING
  dateISO: string | null;
  onPickDate: (yyyyMmDd: string) => void;
  timeSlots: TimeSlot[];
  waitlistPanel?: React.ReactNode;
  selectedSlotId: string | null;
  onPickSlot: (slotId: string) => void;
  onBack: () => void;
  onDone: () => void;
}) {
  const { t, i18n } = useTranslation();
  const hasDate = !!dateISO;
  const hasTime = !!selectedSlotId;
  const language = i18n.resolvedLanguage || i18n.language || "en";
  const dateLocale = React.useMemo(() => getDateLocale(language), [language]);
  const luxonLocale = React.useMemo(() => getLuxonLocale(language), [language]);

  // Month state for decoupled header (mirror controlled prop)
  const initialMonth = monthJSDate ?? (dateISO ? new Date(dateISO) : new Date());
  const [month, setMonth] = useState<Date>(new Date(initialMonth.getFullYear(), initialMonth.getMonth(), 1));

  useEffect(() => {
    if (monthJSDate) {
      setMonth(new Date(monthJSDate.getFullYear(), monthJSDate.getMonth(), 1));
    } else if (dateISO) {
      const d = new Date(dateISO);
      setMonth(new Date(d.getFullYear(), d.getMonth(), 1));
    }
  }, [monthJSDate, dateISO]);

  // Disabled predicate: days not present in enabledDateISOSet are disabled
  const isDisabled = React.useCallback((d: Date) => {
    const iso = DateTime.fromJSDate(d).toISODate();
    return !enabledDateISOSet.has(iso);
  }, [enabledDateISOSet]);

  // Clamp initial month so it can’t be earlier than “now” month
  React.useEffect(() => {
    const base = DateTime.fromJSDate(new Date()).startOf("month");
    const current = DateTime.fromJSDate(month).startOf("month");
    if (current < base) {
      const js = base.toJSDate();
      setMonth(js);
      onMonthChange?.(js);
    }
    // run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build forward-only month options (12 ahead by default)
  const MONTHS_AHEAD = 6;
  const baseMonth = DateTime.fromJSDate(new Date()).startOf("month");

  const monthOptions = React.useMemo(
    () =>
      Array.from({ length: MONTHS_AHEAD }, (_, i) => {
        // build via native Date to avoid typing issues with Duration objects
        const js = new Date(baseMonth.year, baseMonth.month - 1 + i, 1);
        const dt = DateTime.fromJSDate(js);
        return {
          // unique value across years
          value: dt.toFormat("yyyy-LL"),
          label: dt.setLocale(luxonLocale).toFormat("LLLL yyyy"),
          jsDate: dt.toJSDate(),
        };
      }),
    // memo key
    [baseMonth.toISO(), luxonLocale]
  );

  // Current select value always “yyyy-LL”
  const selectValue = React.useMemo(
    () => DateTime.fromJSDate(month).toFormat("yyyy-LL"),
    [month]
  );


  return (
    <div className="bg-background h-[100dvh] min-h-[100dvh]">
      <div className="w-full h-full mx-auto sm:max-w-[420px] md:max-w-[520px]">
        <Card className="bg-card ios-shadow border-0 h-full flex flex-col sm:rounded-2xl sm:overflow-hidden">
          {/* Header */}
          <div className="p-4 border-b border-border/10 flex items-center gap-4">
            <Button variant="ghost" size="sm" className="p-2 h-auto rounded-full" onClick={onBack}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h2 className="text-lg font-semibold">{t("widget.datetime.choose", "Choose date & time")}</h2>
          </div>

          {/* Scrollable content; bottom padding equals sticky bar height + safe area */}
          <div
            className="flex-1 overflow-y-auto p-6"
            style={{ paddingBottom: "calc(88px + env(safe-area-inset-bottom))" }}
          >
            {/* Calendar (full-width DayPicker) */}
            <div className="p-0">
              {/*
                Custom header: hide DayPicker's built-in caption/nav and render our own
                month dropdown + prev/next controls so the nav buttons don't overlap the caption.
              */}
              {/* Header controls: [Prev] [Month Select] [Next] (RU). No year, no labels. */}
              <div className="mb-3 flex items-center justify-center gap-2">
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => {
                    const dt = DateTime.fromJSDate(month);
                    const newMonth = dt.month === 1 ? 12 : dt.month - 1;
                    const newYear = dt.month === 1 ? dt.year - 1 : dt.year;
                    const js = DateTime.fromObject({ year: newYear, month: newMonth, day: 1 }).toJSDate();
                    setMonth(js);
                    onMonthChange?.(js);
                  }}
                  aria-label={t("widget.datetime.previous_month", "Previous month")}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>

                {/* Month dropdown only — Russian month names (forward-only + styled) */}
                <Select
                  value={selectValue}
                  onValueChange={(val) => {
                    const [y,mm] = String(val).split("-");
                    const dt = DateTime.fromObject({ year: Number(y), month: Number(mm), day: 1 });
                    const js = dt.toJSDate();
                    setMonth(js);
                    onMonthChange?.(js);
                  }}
                >
                  <SelectTrigger className="w-[160px] h-10 bg-white border rounded-lg shadow-sm px-3">
                    <SelectValue placeholder={t("widget.datetime.month", "Month")} />
                  </SelectTrigger>

                  <SelectContent
                    position="popper"
                    sideOffset={8}
                    align="start"
                    className="bg-white dark:bg-white border shadow-xl rounded-xl p-0 overflow-hidden"
                  >
                    <div className="max-h-72 overflow-auto divide-y">
                      {monthOptions.map((opt) => (
                        <SelectItem
                          key={opt.value}
                          value={opt.value}
                          className="px-3 py-2 h-auto leading-6 cursor-pointer hover:bg-accent/10 focus:bg-accent/10 data-[state=checked]:bg-accent data-[state=checked]:text-accent-foreground [&>span.absolute]:opacity-0"
                        >
                          {opt.label}
                        </SelectItem>
                      ))}
                    </div>
                  </SelectContent>
                </Select>

                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => {
                    const dt = DateTime.fromJSDate(month);
                    const newMonth = dt.month === 12 ? 1 : dt.month + 1;
                    const newYear = dt.month === 12 ? dt.year + 1 : dt.year;
                    const js = DateTime.fromObject({ year: newYear, month: newMonth, day: 1 }).toJSDate();
                    setMonth(js);
                    onMonthChange?.(js);
                  }}
                  aria-label={t("widget.datetime.next_month", "Next month")}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>

              <Calendar
                locale={dateLocale}
                month={month}
                onMonthChange={(m: Date) => {
                  setMonth(m);
                  // normalize month -> first-of-month date to container
                  onMonthChange?.(new Date(m.getFullYear(), m.getMonth(), 1));
                }}
                mode="single"
                selected={dateISO ? new Date(dateISO) : undefined}
                onSelect={(d) => {
                  if (d instanceof Date && !isDisabled(d)) {
                    onPickDate(DateTime.fromJSDate(d).toISODate());
                  }
                }}
                disabled={isDisabled}
                className="w-full"
                classNames={{
                  root: "w-full",
                  months: "w-full",
                  month: "w-full",

                  /* Hide built-in caption/nav — we render a custom header.
                     (month_caption is the v9 key; without it the shared
                     ui/calendar.tsx default would leave an empty h-7 strip) */
                  month_caption: "hidden",
                  caption_label: "hidden",
                  nav: "hidden",

                  /* widget.css targets td.rdp-selected — pin the default
                     class so the shared calendar's Tailwind `selected`
                     override doesn't replace it; outside/disabled stay
                     unstyled as before (styling lives in widget.css) */
                  selected: "rdp-selected",
                  outside: "rdp-outside",
                  disabled: "rdp-disabled",

                  /* Use the REAL class keys your build outputs */
                  month_grid: "w-full table-fixed border-collapse", // <table>
                  weekdays: "w-full",                                // <thead>
                  weekday: "text-center text-xs font-medium text-muted-foreground py-2",
                  weeks: "",                                         // <tbody>
                  week: "",                                          // <tr>

                  /* IMPORTANT: do NOT change display on <td> */
                  day: "p-0 m-0 text-center align-middle",           // <td> (no w-full, no grid)

                  /* Style the clickable button instead */
                  day_button: "w-10 h-10 inline-flex items-center justify-center rounded-full cursor-pointer",

                  /* Optional: apply extra class on “today” (usually lands on <td>) */
                  today: "", // we’ll style via CSS below to be robust
                }}
              />
            </div>

            {/* Empty-state vs Slots */}
            {loading ? (
              <div className="px-0 text-center text-muted-foreground">
                {t("widget.specialists.loading_availability", "Loading available time...")}
              </div>
            ) : error ? (
              <div className="px-0 text-center text-muted-foreground">
                {t("widget.specialists.error_availability", "Could not load availability. Please try again later.")}
              </div>
            ) : !hasDate ? (
              <div className="px-0 text-center text-muted-foreground">
                {showNoSlotsToday ? (
                  <>
                    {t("widget.datetime.no_slots_today", "There are no available slots today.")} <br />
                    <Button variant="ghost" className="mt-2" onClick={onGoToNearestDate}>
                      {t("widget.datetime.nearest_date", "Go to the nearest date")}
                    </Button>
                  </>
                ) : (
                  <>{t("widget.datetime.pick_date", "Choose a date")}</>
                )}
                {waitlistPanel}
              </div>
            ) : (
              <div className="px-0">
                <div className="mb-3 text-sm text-muted-foreground">{t("widget.datetime.available_time", "Available time:")}</div>
                <div className="flex flex-wrap gap-2">
                  {timeSlots.map((slot) => (
                    <Button
                      key={slot.id}
                      variant={selectedSlotId === slot.id ? "default" : "outline"}
                      size="sm"
                      className={
                        slot.lastHour
                          ? "h-9 rounded-full border-emerald-500 text-emerald-700 data-[pressed=true]:text-inherit"
                          : "h-9 rounded-full"
                      }
                      onClick={() => onPickSlot(slot.id)}
                      aria-pressed={selectedSlotId === slot.id}
                    >
                      {slot.label}
                      {slot.lastHour && (
                        <span className="ml-1 rounded bg-emerald-100 px-1 text-[10px] font-bold text-emerald-700">
                          −{slot.lastHour.discount_pct}%
                        </span>
                      )}
                    </Button>
                  ))}
                  {timeSlots.length === 0 && (
                    <div className="text-sm text-muted-foreground">{t("widget.datetime.no_slots_date", "There are no available slots for this date.")}</div>
                  )}
                </div>
                {waitlistPanel}
              </div>
            )}
          </div>

          {/* Sticky Done CTA */}
          <div
            className="sticky bottom-0 inset-x-0 z-20 border-t border-border/10 bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80"
            style={{ paddingBottom: "max(env(safe-area-inset-bottom), 16px)" }}
          >
            <div className="p-4 pt-3">
              <Button className="w-full h-12 rounded-xl font-semibold" onClick={onDone} disabled={!hasTime}>
                {t("widget.common.done", "Done")}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
